/**
 * merge(object, element, path)
 *
 * Mutates the provided object by adding or overwriting the value at the given path
 * with the provided element. Supports dot notation, slash notation, and numeric
 * array indexes. A trailing [] on the final segment (e.g. "items[]") is supported
 * as a convenience for array assignments:
 *  - If element is an array: replace the entire array at that path
 *  - If element is not an array: append the element to the array (creating it if needed)
 *
 * Notes:
 * - Path parsing is intentionally compatible with typical resolve-like notation for
 *   dot/slash and numeric indexes (e.g. "a.b[2].c" or "a/b/2/c").
 * - Wildcard [] is only supported at the END of the path (e.g. "items[]"). Using []
 *   in the middle of the path to broadcast to all elements is NOT supported.
 * - Deep merge behavior:
 *   - If both the existing target value and the provided element are plain objects,
 *     a deep merge is performed (recursively).
 *   - If the element is an array, the array at the path is replaced (unless using
 *     trailing [] where appending is allowed for non-array element).
 *   - For primitives or mismatched types, the value is overwritten.
 *
 * @param {Object|Array} object - The object to be transformed (mutated in place).
 * @param {*} element - The value to merge into the object at the provided path.
 * @param {string} path - The path within object to add or overwrite.
 * @returns {Object|Array} Returns the same object reference after mutation.
 */
function merge(object, element, path) {
  if (object === null || object === undefined) {
    throw new Error("merge: 'object' must be a non-null object or array.");
  }
  if (typeof path !== 'string' || path.length === 0) {
    // If no path provided, and both are objects, deep merge into root; else replace root keys accordingly
    if (isPlainObject(object) && isPlainObject(element)) {
      deepMerge(object, element);
      return object;
    }
    // If root is array and element is array, replace contents
    if (Array.isArray(object) && Array.isArray(element)) {
      object.length = 0;
      object.push(...element);
      return object;
    }
    // Otherwise, we cannot replace the root reference; instruct to provide a path
    throw new Error("merge: 'path' must be a non-empty string when replacing non-object root or mismatched types.");
  }

  const cleanPath = stripTemplateBraces(path);
  const tokens = tokenizePath(cleanPath);

  // Handle trailing wildcard '[]' if present
  const lastToken = tokens[tokens.length - 1];
  const hasTrailingWildcard = lastToken && lastToken.type === 'wildcard';
  if (hasTrailingWildcard && tokens.length === 1) {
    // Path like "items[]" is invalid without a key name
    throw new Error("merge: invalid path. Trailing [] requires a preceding key (e.g., 'items[]').");
  }

  // Validate that wildcard only appears as the final token
  if (tokens.some((t, i) => t.type === 'wildcard' && i !== tokens.length - 1)) {
    throw new Error("merge: wildcard [] is only supported at the end of the path (e.g., 'items[]').");
  }

  // Walk down to parent of the leaf (or parent of the array for trailing [])
  const parentInfo = getParentContainer(object, tokens, hasTrailingWildcard ? 2 : 1);
  const { parent, keyOrIndex } = parentInfo;

  if (hasTrailingWildcard) {
    // Behavior for '...items[]'
    // parent[keyOrIndex] must be an array (create if needed)
    if (!Array.isArray(parent[keyOrIndex])) {
      parent[keyOrIndex] = [];
    }
    if (Array.isArray(element)) {
      // Replace entire array
      parent[keyOrIndex].length = 0;
      parent[keyOrIndex].push(...element);
    } else {
      // Append single element
      parent[keyOrIndex].push(element);
    }
    return object;
  }

  // Standard leaf assignment/merge
  if (typeof keyOrIndex === 'number') {
    // Ensure array at parent
    if (!Array.isArray(parent)) {
      // If parent is undefined or non-array, convert to array (best effort)
      throw new Error("merge: path points to an array index but parent is not an array.");
    }
    parent[keyOrIndex] = mergeValues(parent[keyOrIndex], element);
  } else {
    // key on object
    // Ensure container exists
    if (parent[keyOrIndex] === undefined) {
      // Create container only when needed for deep merge, else direct assign below
      // If element is a plain object, create empty object and deep merge for immutability semantics
      if (isPlainObject(element)) {
        parent[keyOrIndex] = {};
      }
    }
    parent[keyOrIndex] = mergeValues(parent[keyOrIndex], element);
  }

  return object;
}

/**
 * Merge two values based on their types:
 * - If both are plain objects: deep merge into target (creating object if undefined)
 * - If source is array: replace target with array
 * - Otherwise: overwrite
 */
function mergeValues(targetVal, sourceVal) {
  if (isPlainObject(targetVal) && isPlainObject(sourceVal)) {
    // Mutate targetVal with deep merge
    deepMerge(targetVal, sourceVal);
    return targetVal;
  }
  if (isPlainObject(sourceVal)) {
    // Target missing or not plain object -> use a new object and deep merge source in
    const base = isPlainObject(targetVal) ? targetVal : {};
    deepMerge(base, sourceVal);
    return base;
  }
  if (Array.isArray(sourceVal)) {
    // Replace array
    return sourceVal.slice();
  }
  // Primitives or mismatched types: overwrite
  return sourceVal;
}

/**
 * Deep merge source into target (both plain objects). Mutates target.
 */
function deepMerge(target, source) {
  for (const key in source) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    const srcVal = source[key];
    const tgtVal = target[key];

    if (isPlainObject(srcVal) && isPlainObject(tgtVal)) {
      deepMerge(tgtVal, srcVal);
    } else if (isPlainObject(srcVal)) {
      target[key] = deepCloneObject(srcVal);
    } else if (Array.isArray(srcVal)) {
      target[key] = srcVal.slice();
    } else {
      target[key] = srcVal;
    }
  }
  return target;
}

function isPlainObject(val) {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
}

function deepCloneObject(obj) {
  const out = {};
  for (const k in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
    const v = obj[k];
    if (isPlainObject(v)) {
      out[k] = deepCloneObject(v);
    } else if (Array.isArray(v)) {
      out[k] = v.slice();
    } else {
      out[k] = v;
    }
  }
  return out;
}

function stripTemplateBraces(path) {
  const pathStr = String(path);
  if (pathStr.startsWith('${') && pathStr.endsWith('}')) {
    return pathStr.slice(2, -1);
  }
  return pathStr;
}

/**
 * Tokenize a path supporting:
 * - Dot or slash separators (mixed usage is normalized to dot)
 * - Property names (e.g., "a", "settings", "lines")
 * - Numeric indexes via brackets (e.g., "[0]", "[12]")
 * - Trailing wildcard "[]" only at end (e.g., "items[]")
 *
 * Returns an array of tokens:
 * - { type: 'prop', key: string }
 * - { type: 'index', index: number }
 * - { type: 'wildcard' }  // only allowed as the last token
 */
function tokenizePath(path) {
  // Normalize separators: convert / to .
  const normalized = path.replace(/\//g, '.');

  // Split on dots while preserving bracket expressions in each part
  const rawParts = normalized.split('.').filter(p => p.length > 0);

  const tokens = [];
  for (const part of rawParts) {
    // e.g., "items[]", "b[2][3]", "name", "arr[10]"
    const re = /([^\[\]]+)|\[(.*?)\]/g;
    let m;
    let seenBase = false;
    while ((m = re.exec(part)) !== null) {
      if (m[1] !== undefined) {
        // Base property
        tokens.push({ type: 'prop', key: m[1] });
        seenBase = true;
      } else {
        // Bracket expression
        const inner = m[2]; // could be '', or digits, or invalid
        if (inner === '') {
          // Wildcard []
          tokens.push({ type: 'wildcard' });
        } else if (/^\d+$/.test(inner)) {
          tokens.push({ type: 'index', index: parseInt(inner, 10) });
        } else {
          throw new Error(`merge: invalid bracket expression '[${inner}]' in path.`);
        }
      }
    }
    if (!seenBase && part.startsWith('[')) {
      // Part like "[0]" without preceding prop is allowed (array at root): e.g., "[0].name"
      // No action needed; tokens already include index
    }
  }

  if (tokens.length === 0) {
    throw new Error("merge: invalid empty path.");
  }
  return tokens;
}

/**
 * Traverse down the object according to tokens, creating containers as needed,
 * and return the parent and the final keyOrIndex to assign into.
 *
 * If cutFromEnd = 1: stop before the last token and return parent of the leaf.
 * If cutFromEnd = 2 (used for trailing wildcard): stop before the last two tokens
 * (i.e. before {prop} + {wildcard}), returning the container for the array key.
 */
function getParentContainer(root, tokens, cutFromEnd = 1) {
  // Allow single-token paths (e.g., "netSalesPrice") where the parent is the root object.
  // We only need to ensure that end = tokens.length - cutFromEnd is not negative.
  if (!Array.isArray(tokens) || (tokens.length - cutFromEnd) < 0) {
    throw new Error("merge: path is too short to locate parent container.");
  }
  const end = tokens.length - cutFromEnd;
  let current = root;

  for (let i = 0; i < end; i++) {
    const t = tokens[i];
    const next = tokens[i + 1];

    if (t.type === 'prop') {
      if (current[t.key] === undefined || current[t.key] === null) {
        // Create container based on the next token type
        if (next && next.type === 'index') {
          current[t.key] = [];
        } else if (next && next.type === 'prop') {
          current[t.key] = {};
        } else if (next && next.type === 'wildcard') {
          // Next is wildcard, we need an array at this prop
          current[t.key] = [];
        } else {
          // Default to object
          current[t.key] = {};
        }
      }
      current = current[t.key];
      continue;
    }

    if (t.type === 'index') {
      if (!Array.isArray(current)) {
        // Convert into array (best effort)
        throw new Error("merge: encountered numeric index in path but current container is not an array.");
      }
      const idx = t.index;
      // Extend array if needed
      if (current.length <= idx) {
        current.length = idx + 1;
      }
      if (current[idx] === undefined || current[idx] === null) {
        // Create container based on next token
        if (next && next.type === 'index') {
          current[idx] = [];
        } else if (next && next.type === 'prop') {
          current[idx] = {};
        } else if (next && next.type === 'wildcard') {
          current[idx] = [];
        } else {
          current[idx] = {};
        }
      }
      current = current[idx];
      continue;
    }

    if (t.type === 'wildcard') {
      // Only allowed at the very end; earlier validation should prevent this
      throw new Error("merge: wildcard [] is only supported at the end of the path.");
    }
  }

  // Determine final key or index
  const finalToken = tokens[end];
  if (finalToken.type === 'prop') {
    return { parent: current, keyOrIndex: finalToken.key };
  }
  if (finalToken.type === 'index') {
    // Ensure array exists
    if (!Array.isArray(current)) {
      throw new Error("merge: final token is an index but parent container is not an array.");
    }
    const idx = finalToken.index;
    if (current.length <= idx) {
      current.length = idx + 1;
    }
    return { parent: current, keyOrIndex: idx };
  }
  if (finalToken.type === 'wildcard') {
    // For trailing wildcard, the parent is actually the container referenced by the previous prop
    const prevToken = tokens[end - 1];
    if (!prevToken || prevToken.type !== 'prop') {
      throw new Error("merge: trailing wildcard must follow a property name (e.g., 'items[]').");
    }
    return { parent: current, keyOrIndex: prevToken.key };
  }

  throw new Error("merge: unrecognized final token.");
}

export default merge;
